/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Checkbox } from '@mui/material';
import React from 'react';
import { AlertIcon, TooltipSelect } from '@opengeoweb/shared';
import type { EDRFeatureLayerOptions, LayerProps } from '@opengeoweb/webmap';
import { showNoLayers } from './RenderLayers';
import { useCoreTranslation } from '../../../../../utils/i18n';

export interface RenderEDRLayersProps {
  onChangeLayerOptions: (options: EDRFeatureLayerOptions) => void;
  selectedParams: string[];
  layers: LayerProps[];
  layerName: string;
  collectionId: string;
  isEnabled?: boolean;
  tooltipPrefix?: string;
  skipLocalStyling?: boolean;
}

export const getRenderEDRLayersValues = (
  selectedParams: string[],
  layers: LayerProps[],
): {
  currentValues: string[];
  extendedLayers: LayerProps[];
} => {
  const paramArray = selectedParams.reduce<{
    found: string[];
    notFound: string[];
  }>(
    (obj, param) => {
      const serviceLayer = layers.find((layer) => layer.name === param);
      if (!serviceLayer) {
        return {
          notFound: obj.notFound.concat([param]),
          found: [...obj.found],
        };
      }
      return {
        found: obj.found.concat([param]),
        notFound: [...obj.notFound],
      };
    },
    { found: [], notFound: [] },
  );

  if (paramArray.notFound.length > 0) {
    return {
      currentValues: [...paramArray.found, paramArray.notFound.join(',')],
      extendedLayers: [
        ...layers,
        {
          name: paramArray.notFound.join(','),
          title: paramArray.notFound.join(','),
          leaf: true,
          path: [],
        },
      ],
    };
  }
  return {
    currentValues: selectedParams,
    extendedLayers: layers,
  };
};

const RenderEDRLayers: React.FC<RenderEDRLayersProps> = ({
  onChangeLayerOptions,
  selectedParams,
  layers = [],
  layerName,
  collectionId,
  isEnabled = true,
  tooltipPrefix,
  skipLocalStyling,
}: RenderEDRLayersProps) => {
  const { t } = useCoreTranslation();
  const nonEmptyTooltipPrefix =
    tooltipPrefix ??
    (selectedParams.length === 1
      ? `${t('layermanager-layer')}: `
      : `${t('layermanager-layer-multi')}: `);

  if (!layers?.length) {
    return showNoLayers(t);
  }

  const selectLayer = (event: SelectChangeEvent<unknown>): void => {
    event.stopPropagation();
    // Ensure after deselecting all, the empty value gets removed
    const parameterNames = (event.target.value as string[]).filter(
      (param) => param !== '',
    );
    onChangeLayerOptions({ collectionId, parameterNames });
  };

  const { currentValues, extendedLayers } = getRenderEDRLayersValues(
    selectedParams,
    layers,
  );

  const list = extendedLayers.map((layer) => ({
    value: layer.name!,
  }));

  const edrText = t('layermanager-EDR');
  const tooltipNames = currentValues.map((value) => {
    const layer = extendedLayers.find((layer) => value === layer.name);
    return layer?.title || layer?.name;
  });
  const tooltip = `${nonEmptyTooltipPrefix} ${tooltipNames
    .sort((paramA, paramB) => (paramA ?? '').localeCompare(paramB ?? ''))
    .join(', ')}`;
  return (
    <TooltipSelect
      disableUnderline
      inputProps={{
        SelectDisplayProps: {
          'data-testid': 'selectEDRLayer',
        },
      }}
      isEnabled={isEnabled}
      list={list}
      multiple
      onChange={selectLayer}
      renderValue={() => edrText + layerName}
      skipLocalStyling={skipLocalStyling}
      tooltip={tooltip}
      value={currentValues}
    >
      <MenuItem disabled>{t('layermanager-layer')}</MenuItem>
      {extendedLayers.map(
        (layerFromServiceLayers: LayerProps, currentIndex) => (
          <MenuItem
            disabled={!layerFromServiceLayers.leaf!} // show categories as disabled
            key={layerFromServiceLayers.name}
            sx={{
              '&.MuiMenuItem-root.Mui-selected': {
                background: 'none',
                color: 'geowebColors.typographyAndIcons.text',
              },
            }}
            value={layerFromServiceLayers.name!}
          >
            <Checkbox
              checked={currentValues.includes(layerFromServiceLayers.name!)}
            />
            {layerFromServiceLayers.title}
            {currentIndex === layers.length && <AlertIcon severity="error" />}
          </MenuItem>
        ),
      )}
    </TooltipSelect>
  );
};

export default RenderEDRLayers;
